import React from 'react';
import { 
  Box, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Chip, 
  Autocomplete, 
  TextField,
  useTheme,
  Button,
  Typography,
  Switch,
  FormGroup
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ToggleButton from '../common/ToggleButton';

const CartFilter = ({
  sectionName,
  cartFilterType,
  setCartFilterType,
  selectedCartTypes,
  handleCartTypesChange,
  selectedCarts,
  handleSpecificCartsChange,
  cartsList,
  cartTypeOptions,
  onFilterChange,
  isYAxisPercentage,
  handleYAxisPercentageChange
}) => {
  const theme = useTheme();
  
  const handleFilterTypeChange = (e) => {
    setCartFilterType(e.target.value);
    // Remove automatic data fetch - only update local state
  };

  const getSectionDisplayName = (section) => {
    const displayNames = {
      availability: 'Availability',
      onService: 'On Service',
      waitingTime: 'Waiting Time',
      trips: 'Trips'
    };
    return displayNames[section] || section;
  };

  // Get custom toggle button label based on section
  const getToggleButtonLabel = () => {
    switch (sectionName) {
      case 'availability':
        return 'Show as Percentage - out of interval minutes count';
      case 'onService':
        return 'Show as Percentage - out of interval minutes count';
      case 'waitingTime':
        return 'Show as Percentage - out of 5 min threshold';
      default:
        return 'Show as Percentage';
    }
  };

  return (
    <Box>
      {/* Filter Content */}
      <Box sx={{ 
        p: 3, 
        background: 'transparent'
      }}>
        <Grid container spacing={3}>
          {/* Filter Type Selection */}
          <Grid item xs={12}>
            <FormControl>
              <RadioGroup
                row
                value={cartFilterType}
                onChange={handleFilterTypeChange}
                name={`${sectionName}-cart-filter-type`}
              >
                <FormControlLabel 
                  value="all" 
                  control={<Radio size="small" />} 
                  label="All Carts by Type" 
                />
                <FormControlLabel 
                  value="specific" 
                  control={<Radio size="small" />} 
                  label="Specific Carts" 
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* All Carts Filter */}
          {cartFilterType === 'all' && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Cart Types</InputLabel>
                    <Select
                      multiple
                      value={selectedCartTypes}
                      onChange={(e) => handleCartTypesChange(e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const option = cartTypeOptions.find(opt => opt.value === value);
                            return (
                              <Chip 
                                key={value} 
                                label={option?.label || value} 
                                size="small" 
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {cartTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Specific Carts Filter */}
          {cartFilterType === 'specific' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                size="small"
                value={selectedCarts}
                onChange={(event, newValue) => {
                  if (newValue.length <= 3) {
                    handleSpecificCartsChange(newValue);
                  }
                }}
                options={cartsList}
                getOptionLabel={(option) => `${option.name} (${option.isActive ? 'Active' : 'Inactive'})`}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Specific Carts (max 3)"
                    placeholder="Choose carts..."
                    helperText={`${selectedCarts.length}/3 carts selected`}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={`${option.name} (${option.isActive ? 'Active' : 'Inactive'})`}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                sx={{ width: '100%' }}
              />
            </Grid>
          )}
        </Grid>
                <Box sx={{ mt: 3 }}>
          <ToggleButton
            checked={isYAxisPercentage}
            onChange={handleYAxisPercentageChange}
            label={getToggleButtonLabel()}
            size="medium"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CartFilter;
